import Phaser from 'phaser';
import { CONSTANTS } from '../constants';
import type { IAssetLoader } from './AssetLoader';
import { SeededRandom } from '../utils/SeededRandom';

// Helper: convert hex string to number
function hexToNum(hex: string): number {
  return parseInt(hex.replace('#', ''), 16);
}

export class AssetGenerator implements IAssetLoader {
  private scene: Phaser.Scene;
  private cacheUsed = false;
  private externalAssets: Map<string, string> = new Map();
  private proceduralAssets: Map<string, { width: number; height: number; drawFn: (g: Phaser.GameObjects.Graphics) => void }> = new Map();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  registerSprite(key: string, width: number, height: number, drawFn: (g: Phaser.GameObjects.Graphics) => void): void {
    this.proceduralAssets.set(key, { width, height, drawFn });
  }

  registerExternalSprite(key: string, path: string): void {
    this.externalAssets.set(key, path);
  }

  loadExternalAssets(scene: Phaser.Scene): void {
    for (const [key, path] of this.externalAssets) {
      scene.load.image(key, path);
    }
  }

  generateProceduralAssets(scene: Phaser.Scene): void {
    for (const [key, { drawFn }] of this.proceduralAssets) {
      const g = scene.make.graphics({ x: 0, y: 0 });
      drawFn(g);
      (g as any).generateTexture(key, 64, 64);
      (g as any).destroy();
    }
  }

  createSprite(scene: Phaser.Scene, x: number, y: number, key: string, scale?: number): Phaser.GameObjects.Sprite {
    const sprite = scene.add.sprite(x, y, key).setScrollFactor(0);
    if (scale) sprite.setScale(scale);
    return sprite;
  }

  generateAll(): void {
    if (this.cacheUsed) return;
    this.generateCharacters();
    this.generateBackground();
    this.generatePlatforms();
    this.generatePowerups();
    this.generateStone();
    this.generateCheckpoints();
    this.generateExitGoal();
    this.cacheUsed = true;
  }

  private generateCharacters(): void {
    const player = this.createPlayerSprite();
    const run2 = this.createPlayerRunFrame2();
    run2.generateTexture('player_run2', 36, 56);
    run2.destroy();
    const run3 = this.createPlayerRunFrame3();
    run3.generateTexture('player_run3', 36, 56);
    run3.destroy();
    player.generateTexture('player_idle', 36, 56);

    const run0 = this.createPlayerRunFrame0();
    run0.generateTexture('player_run', 36, 56);
    run0.destroy();

    const jump = this.createPlayerJumpSprite();
    jump.generateTexture('player_jump', 36, 56);
    jump.destroy();

    const fall = this.createPlayerFallSprite();
    fall.generateTexture('player_fall', 36, 56);
    fall.destroy();

    const dead = this.createPlayerDeadSprite();
    dead.generateTexture('player_dead', 36, 56);
    dead.destroy();

    const stone = this.createPlayerStoneThrowSprite();
    stone.generateTexture('player_stone', 36, 56);
    stone.destroy();

    const pcc = this.createEnemySprite('pcc');
    pcc.generateTexture('pcc_idle', 40, 56);
    pcc.destroy();

    const pccRun = this.createEnemyRunFrame0('pcc');
    pccRun.generateTexture('pcc_run', 40, 56);
    pccRun.destroy();

    const pccRun2 = this.createEnemyRunFrame2('pcc');
    pccRun2.generateTexture('pcc_run2', 40, 56);
    pccRun2.destroy();

    const pccRun3 = this.createEnemyRunFrame3('pcc');
    pccRun3.generateTexture('pcc_run3', 40, 56);
    pccRun3.destroy();

    const pccAtk = this.createEnemyAttackSprite('pcc');
    pccAtk.generateTexture('pcc_attack', 40, 56);
    pccAtk.destroy();

    const fmc = this.createEnemySprite('fmc');
    fmc.generateTexture('fmc_idle', 40, 56);
    fmc.destroy();

    const fmcRun = this.createEnemyRunFrame0('fmc');
    fmcRun.generateTexture('fmc_run', 40, 56);
    fmcRun.destroy();

    const fmcRun2 = this.createEnemyRunFrame2('fmc');
    fmcRun2.generateTexture('fmc_run2', 40, 56);
    fmcRun2.destroy();

    const fmcRun3 = this.createEnemyRunFrame3('fmc');
    fmcRun3.generateTexture('fmc_run3', 40, 56);
    fmcRun3.destroy();

    const fmcAtk = this.createEnemyAttackSprite('fmc');
    fmcAtk.generateTexture('fmc_attack', 40, 56);
    fmcAtk.destroy();

    const ujc = this.createEnemySprite('ujc');
    ujc.generateTexture('ujc_idle', 40, 56);
    ujc.destroy();

    const ujcRun = this.createEnemyRunFrame0('ujc');
    ujcRun.generateTexture('ujc_run', 40, 56);
    ujcRun.destroy();

    const ujcRun2 = this.createEnemyRunFrame2('ujc');
    ujcRun2.generateTexture('ujc_run2', 40, 56);
    ujcRun2.destroy();

    const ujcRun3 = this.createEnemyRunFrame3('ujc');
    ujcRun3.generateTexture('ujc_run3', 40, 56);
    ujcRun3.destroy();

    const ujcAtk = this.createEnemyAttackSprite('ujc');
    ujcAtk.generateTexture('ujc_attack', 40, 56);
    ujcAtk.destroy();

    const pccDead = this.createEnemyDeadSprite('pcc');
    pccDead.generateTexture('pcc_dead', 40, 56);
    pccDead.destroy();

    const fmcDead = this.createEnemyDeadSprite('fmc');
    fmcDead.generateTexture('fmc_dead', 40, 56);
    fmcDead.destroy();

    const ujcDead = this.createEnemyDeadSprite('ujc');
    ujcDead.generateTexture('ujc_dead', 40, 56);
    ujcDead.destroy();

    const tourist = this.createNpcSprite('tourist');
    tourist.generateTexture('tourist', 36, 56);
    tourist.destroy();

    const pioneer = this.createNpcSprite('pioneer');
    pioneer.generateTexture('pioneer', 36, 56);
    pioneer.destroy();

    const civilian = this.createNpcSprite('civilian');
    civilian.generateTexture('civilian', 36, 56);
    civilian.destroy();

    this.cacheUsed = true;
  }

  private createPlayerSprite(): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 18;
    const skinColor = hexToNum('#d4a574');
    const shirtColor = hexToNum('#4488ff');
    const pantsColor = hexToNum('#334466');
    const shoeColor = hexToNum('#553322');

    // Head
    (g as any).fillStyle(skinColor);
    (g as any).fillCircle(cx, 12, 8);
    (g as any).lineStyle(1, hexToNum('#333333'));
    (g as any).strokeCircle(cx, 12, 8);

    // Hair (dark)
    (g as any).fillStyle(hexToNum('#222222'));
    (g as any).fillCircle(cx, 9, 7);
    (g as any).fillRect(cx - 7, 5, 14, 5);

    // Eyes
    (g as any).fillStyle(0xffffff);
    (g as any).fillCircle(cx - 3, 11, 2);
    (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222'));
    (g as any).fillCircle(cx - 2, 11, 1);
    (g as any).fillCircle(cx + 4, 11, 1);

    // Mouth (determined expression)
    (g as any).lineStyle(1, hexToNum('#333333'));
    g.beginPath();
    g.moveTo(cx - 3, 15);
    g.lineTo(cx + 3, 15);
    g.strokePath();

    // Neck
    (g as any).fillStyle(skinColor);
    (g as any).fillRect(cx - 3, 18, 6, 4);

    // Body (guayabera shirt)
    (g as any).fillStyle(shirtColor);
    (g as any).fillRect(cx - 10, 22, 20, 18);
    (g as any).lineStyle(1, hexToNum('#3355aa'));
    (g as any).strokeRect(cx - 10, 22, 20, 18);

    // Shirt details (guayabera lines)
    (g as any).lineStyle(1, hexToNum('#5599cc'));
    g.beginPath();
    g.moveTo(cx - 5, 26);
    g.lineTo(cx - 5, 38);
    g.strokePath();
    g.beginPath();
    g.moveTo(cx + 5, 26);
    g.lineTo(cx + 5, 38);
    g.strokePath();

    // Buttons
    (g as any).fillStyle(0xffffff);
    (g as any).fillCircle(cx, 28, 1.5);
    (g as any).fillCircle(cx, 33, 1.5);

    // Arms
    (g as any).fillStyle(shirtColor);
    (g as any).fillRect(cx - 14, 23, 5, 14);
    (g as any).fillRect(cx + 9, 23, 5, 14);
    // Hands
    (g as any).fillStyle(skinColor);
    (g as any).fillCircle(cx - 11, 38, 3);
    (g as any).fillCircle(cx + 11, 38, 3);

    // Belt
    (g as any).fillStyle(shoeColor);
    (g as any).fillRect(cx - 10, 38, 20, 3);
    (g as any).fillStyle(hexToNum('#ddaa44'));
    (g as any).fillRect(cx - 2, 38, 4, 3);

    // Legs
    (g as any).fillStyle(pantsColor);
    (g as any).fillRect(cx - 8, 41, 7, 10);
    (g as any).fillRect(cx + 1, 41, 7, 10);

    // Shoes
    (g as any).fillStyle(shoeColor);
    (g as any).fillRect(cx - 9, 49, 8, 5);
    (g as any).fillRect(cx + 1, 49, 8, 5);

    return g;
  }

  private createPlayerRunFrame0(): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 18;
    const skinColor = hexToNum('#d4a574');
    const shirtColor = hexToNum('#4488ff');
    const pantsColor = hexToNum('#334466');
    const shoeColor = hexToNum('#553322');

    (g as any).fillStyle(skinColor); (g as any).fillCircle(cx, 12, 8);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx, 9, 7); (g as any).fillRect(cx - 7, 5, 14, 5);
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);

    (g as any).fillStyle(skinColor); (g as any).fillRect(cx - 3, 18, 6, 4);
    (g as any).fillStyle(shirtColor); (g as any).fillRect(cx - 10, 22, 20, 18);
    (g as any).lineStyle(1, hexToNum('#5599cc'));
    g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
    g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx, 28, 1.5); (g as any).fillCircle(cx, 33, 1.5);

    (g as any).fillStyle(shirtColor); (g as any).fillRect(cx - 14, 23, 5, 14); (g as any).fillRect(cx + 9, 23, 5, 14);
    (g as any).fillStyle(skinColor); (g as any).fillCircle(cx - 11, 38, 3); (g as any).fillCircle(cx + 11, 38, 3);

    (g as any).fillStyle(shoeColor); (g as any).fillRect(cx - 10, 38, 20, 3);
    (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillRect(cx - 2, 38, 4, 3);

    // Legs spread (running frame 0)
    (g as any).fillStyle(pantsColor);
    (g as any).fillRect(cx - 10, 41, 6, 8);
    (g as any).fillRect(cx + 4, 41, 6, 8);
    (g as any).fillStyle(shoeColor);
    (g as any).fillRect(cx - 11, 47, 8, 7);
    (g as any).fillRect(cx + 3, 47, 8, 7);

    return g;
  }

  private createPlayerRunFrame1(): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 18;
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx, 12, 8);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx, 9, 7); (g as any).fillRect(cx - 7, 5, 14, 5);
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillRect(cx - 3, 18, 6, 4);
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 10, 22, 20, 18);
    (g as any).lineStyle(1, hexToNum('#5599cc'));
    g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
    g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx, 28, 1.5); (g as any).fillCircle(cx, 33, 1.5);
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 14, 23, 5, 14); (g as any).fillRect(cx + 9, 23, 5, 14);
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx - 11, 38, 3); (g as any).fillCircle(cx + 11, 38, 3);
    (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(cx - 10, 38, 20, 3);
    (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillRect(cx - 2, 38, 4, 3);
    // Legs together (running frame 1)
    (g as any).fillStyle(hexToNum('#334466')); (g as any).fillRect(cx - 8, 41, 7, 10); (g as any).fillRect(cx + 1, 41, 7, 10);
    (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(cx - 9, 49, 8, 5); (g as any).fillRect(cx + 1, 49, 8, 5);
    return g;
  }

  private createPlayerRunFrame2(): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 18;
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx, 12, 8);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx, 9, 7); (g as any).fillRect(cx - 7, 5, 14, 5);
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillRect(cx - 3, 18, 6, 4);
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 10, 22, 20, 18);
    (g as any).lineStyle(1, hexToNum('#5599cc'));
    g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
    g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx, 28, 1.5); (g as any).fillCircle(cx, 33, 1.5);
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 14, 23, 5, 14); (g as any).fillRect(cx + 9, 23, 5, 14);
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx - 11, 38, 3); (g as any).fillCircle(cx + 11, 38, 3);
    (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(cx - 10, 38, 20, 3);
    (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillRect(cx - 2, 38, 4, 3);
    // Legs spread other direction (running frame 2)
    (g as any).fillStyle(hexToNum('#334466')); (g as any).fillRect(cx - 4, 41, 6, 8); (g as any).fillRect(cx - 8, 41, 6, 8);
    (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(cx + 3, 47, 8, 7); (g as any).fillRect(cx - 11, 47, 8, 7);
    return g;
  }

  private createPlayerRunFrame3(): Phaser.GameObjects.Graphics {
    return this.createPlayerRunFrame1();
  }

  private createPlayerJumpSprite(): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 18;
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx, 12, 8);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx, 9, 7); (g as any).fillRect(cx - 7, 5, 14, 5);
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillRect(cx - 3, 18, 6, 4);
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 10, 22, 20, 18);
    (g as any).lineStyle(1, hexToNum('#5599cc'));
    g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
    g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx, 28, 1.5); (g as any).fillCircle(cx, 33, 1.5);
    // Arms up (jumping)
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 15, 18, 5, 10); (g as any).fillRect(cx + 10, 18, 5, 10);
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx - 12, 17, 3); (g as any).fillCircle(cx + 12, 17, 3);
    // Legs bent (jumping)
    (g as any).fillStyle(hexToNum('#334466')); (g as any).fillRect(cx - 10, 41, 7, 6); (g as any).fillRect(cx + 3, 41, 7, 6);
    (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(cx - 11, 45, 8, 5); (g as any).fillRect(cx + 3, 45, 8, 5);
    return g;
  }

  private createPlayerFallSprite(): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 18;
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx, 12, 8);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx, 9, 7); (g as any).fillRect(cx - 7, 5, 14, 5);
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillRect(cx - 3, 18, 6, 4);
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 10, 22, 20, 18);
    (g as any).lineStyle(1, hexToNum('#5599cc'));
    g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
    g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx, 28, 1.5); (g as any).fillCircle(cx, 33, 1.5);
    // Arms down (falling)
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 14, 23, 5, 14); (g as any).fillRect(cx + 9, 23, 5, 14);
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx - 11, 38, 3); (g as any).fillCircle(cx + 12, 38, 3);
    // Legs extended (falling)
    (g as any).fillStyle(hexToNum('#334466')); (g as any).fillRect(cx - 10, 41, 7, 10); (g as any).fillRect(cx + 3, 41, 7, 10);
    (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(cx - 11, 49, 8, 5); (g as any).fillRect(cx + 3, 49, 8, 5);
    return g;
  }

  private createPlayerDeadSprite(): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 18;
    // Body lying down (horizontal)
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 14, 20, 28, 16);
    (g as any).fillStyle(hexToNum('#334466')); (g as any).fillRect(cx - 14, 22, 28, 12);
    (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillRect(cx - 2, 18, 4, 4);
    // Head
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx + 14, 28, 8);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx + 14, 25, 7); (g as any).fillRect(cx + 7, 21, 14, 5);
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx + 11, 27, 2); (g as any).fillCircle(cx + 17, 27, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx + 10, 27, 1); (g as any).fillCircle(cx + 18, 27, 1);
    // Arms and legs
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 16, 24, 6, 5); (g as any).fillRect(cx + 12, 24, 6, 5);
    (g as any).fillStyle(hexToNum('#334466')); (g as any).fillRect(cx - 16, 30, 6, 5); (g as any).fillRect(cx + 12, 30, 6, 5);
    (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(cx - 18, 32, 6, 5); (g as any).fillRect(cx + 12, 32, 6, 5);
    return g;
  }

  private createEnemyDeadSprite(type: 'pcc' | 'fmc' | 'ujc'): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    // Enemy body lying down (horizontal)
    const bodyColor = type === 'pcc' ? hexToNum('#448844') : type === 'fmc' ? hexToNum('#886644') : hexToNum('#333333');
    const uniformColor = type === 'pcc' ? hexToNum('#448844') : type === 'fmc' ? hexToNum('#886644') : hexToNum('#333333');
    (g as any).fillStyle(bodyColor); (g as any).fillRect(4, 20, 32, 16);
    (g as any).fillStyle(uniformColor); (g as any).fillRect(4, 22, 32, 12);
    // Head
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(32, 28, 8);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(32, 25, 7); (g as any).fillRect(25, 21, 14, 5);
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(29, 27, 2); (g as any).fillCircle(35, 27, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(28, 27, 1); (g as any).fillCircle(36, 27, 1);
    // Arms and legs
    (g as any).fillStyle(bodyColor); (g as any).fillRect(2, 24, 6, 5); (g as any).fillRect(28, 24, 6, 5);
    (g as any).fillStyle(uniformColor); (g as any).fillRect(2, 30, 6, 5); (g as any).fillRect(28, 30, 6, 5);
    (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(0, 32, 6, 5); (g as any).fillRect(30, 32, 6, 5);
    return g;
  }

  private createPlayerStoneThrowSprite(): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 18;
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx, 12, 8);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx, 9, 7); (g as any).fillRect(cx - 7, 5, 14, 5);
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillRect(cx - 3, 18, 6, 4);
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 10, 22, 20, 18);
    (g as any).lineStyle(1, hexToNum('#5599cc'));
    g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
    g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx, 28, 1.5); (g as any).fillCircle(cx, 33, 1.5);
    // Right arm extended (throwing)
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx + 9, 23, 10, 5);
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx + 19, 25, 3);
    // Left arm down
    (g as any).fillStyle(hexToNum('#4488ff')); (g as any).fillRect(cx - 14, 23, 5, 14);
    (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx - 11, 38, 3);
    (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(cx - 10, 38, 20, 3);
    (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillRect(cx - 2, 38, 4, 3);
    (g as any).fillStyle(hexToNum('#334466')); (g as any).fillRect(cx - 8, 41, 7, 10); (g as any).fillRect(cx + 1, 41, 7, 10);
    (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(cx - 9, 49, 8, 5); (g as any).fillRect(cx + 1, 49, 8, 5);
    return g;
  }

  private createEnemySprite(type: 'pcc' | 'fmc' | 'ujc'): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 20;
    const colors = this.getEnemyColors(type);

    // Head
    (g as any).fillStyle(colors.skin);
    (g as any).fillCircle(cx, 12, 8);
    (g as any).lineStyle(1, hexToNum('#333333'));
    (g as any).strokeCircle(cx, 12, 8);

    // Hair/uniform cap
    if (type === 'pcc') {
      (g as any).fillStyle(hexToNum('#445544'));
      (g as any).fillRect(cx - 8, 4, 16, 6);
      (g as any).fillRect(cx - 6, 2, 12, 3);
    } else if (type === 'fmc') {
      (g as any).fillStyle(hexToNum('#665544'));
      (g as any).fillRect(cx - 8, 4, 16, 6);
      (g as any).fillRect(cx - 6, 2, 12, 3);
    } else {
      (g as any).fillStyle(hexToNum('#554433'));
      (g as any).fillRect(cx - 7, 3, 14, 7);
    }

    // Eyes
    (g as any).fillStyle(0xffffff);
    (g as any).fillCircle(cx - 3, 11, 2);
    (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222'));
    (g as any).fillCircle(cx - 2, 11, 1);
    (g as any).fillCircle(cx + 4, 11, 1);

    // Angry mouth
    (g as any).lineStyle(1, hexToNum('#333333'));
    g.beginPath();
    g.moveTo(cx - 3, 15);
    g.lineTo(cx + 3, 15);
    g.strokePath();

    // Neck
    (g as any).fillStyle(colors.skin);
    (g as any).fillRect(cx - 3, 18, 6, 4);

    // Body (uniform)
    (g as any).fillStyle(colors.uniform);
    (g as any).fillRect(cx - 11, 22, 22, 18);
    (g as any).lineStyle(1, colors.uniformDark);
    (g as any).strokeRect(cx - 11, 22, 22, 18);

    // Uniform details
    (g as any).lineStyle(1, colors.uniformDark);
    g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
    g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();

    // Buttons
    (g as any).fillStyle(hexToNum('#ddaa44'));
    (g as any).fillCircle(cx, 28, 1.5);
    (g as any).fillCircle(cx, 33, 1.5);

    // Arms
    (g as any).fillStyle(colors.uniform);
    (g as any).fillRect(cx - 15, 23, 5, 14);
    (g as any).fillRect(cx + 10, 23, 5, 14);
    // Hands
    (g as any).fillStyle(colors.skin);
    (g as any).fillCircle(cx - 12, 38, 3);
    (g as any).fillCircle(cx + 12, 38, 3);

    // Belt
    (g as any).fillStyle(hexToNum('#333333'));
    (g as any).fillRect(cx - 11, 38, 22, 3);
    (g as any).fillStyle(hexToNum('#ddaa44'));
    (g as any).fillRect(cx - 2, 38, 4, 3);

    // Legs (uniform pants)
    (g as any).fillStyle(colors.pants);
    (g as any).fillRect(cx - 9, 41, 7, 10);
    (g as any).fillRect(cx + 2, 41, 7, 10);

    // Boots
    (g as any).fillStyle(hexToNum('#333333'));
    (g as any).fillRect(cx - 10, 49, 9, 5);
    (g as any).fillRect(cx + 1, 49, 9, 5);

    return g;
  }

  private getEnemyColors(type: 'pcc' | 'fmc' | 'ujc'): { skin: number; uniform: number; uniformDark: number; pants: number } {
    switch (type) {
      case 'pcc':
        return { skin: hexToNum('#d4a574'), uniform: hexToNum('#445544'), uniformDark: hexToNum('#334433'), pants: hexToNum('#445544') };
      case 'fmc':
        return { skin: hexToNum('#d4a574'), uniform: hexToNum('#665544'), uniformDark: hexToNum('#554433'), pants: hexToNum('#665544') };
      case 'ujc':
        return { skin: hexToNum('#d4a574'), uniform: hexToNum('#554433'), uniformDark: hexToNum('#443322'), pants: hexToNum('#554433') };
    }
  }

  private createEnemyRunFrame0(type: 'pcc' | 'fmc' | 'ujc'): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 20;
    const colors = this.getEnemyColors(type);
    (g as any).fillStyle(colors.skin); (g as any).fillCircle(cx, 12, 8);
    if (type === 'pcc') { (g as any).fillStyle(hexToNum('#445544')); (g as any).fillRect(cx - 8, 4, 16, 6); (g as any).fillRect(cx - 6, 2, 12, 3); }
    else if (type === 'fmc') { (g as any).fillStyle(hexToNum('#665544')); (g as any).fillRect(cx - 8, 4, 16, 6); (g as any).fillRect(cx - 6, 2, 12, 3); }
    else { (g as any).fillStyle(hexToNum('#554433')); (g as any).fillRect(cx - 7, 3, 14, 7); }
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
    (g as any).lineStyle(1, hexToNum('#333333')); g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();
    (g as any).fillStyle(colors.skin); (g as any).fillRect(cx - 3, 18, 6, 4);
    (g as any).fillStyle(colors.uniform); (g as any).fillRect(cx - 11, 22, 22, 18);
    (g as any).lineStyle(1, colors.uniformDark);
    g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
    g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
    (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillCircle(cx, 28, 1.5); (g as any).fillCircle(cx, 33, 1.5);
    (g as any).fillStyle(colors.uniform); (g as any).fillRect(cx - 15, 23, 5, 14); (g as any).fillRect(cx + 10, 23, 5, 14);
    (g as any).fillStyle(colors.skin); (g as any).fillCircle(cx - 12, 38, 3); (g as any).fillCircle(cx + 12, 38, 3);
    (g as any).fillStyle(hexToNum('#333333')); (g as any).fillRect(cx - 11, 38, 22, 3); (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillRect(cx - 2, 38, 4, 3);
    (g as any).fillStyle(colors.pants); (g as any).fillRect(cx - 10, 41, 6, 8); (g as any).fillRect(cx + 4, 41, 6, 8);
    (g as any).fillStyle(hexToNum('#333333')); (g as any).fillRect(cx - 11, 47, 8, 7); (g as any).fillRect(cx + 3, 47, 8, 7);
    return g;
  }

  private createEnemyRunFrame1(type: 'pcc' | 'fmc' | 'ujc'): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 20;
    const colors = this.getEnemyColors(type);
    (g as any).fillStyle(colors.skin); (g as any).fillCircle(cx, 12, 8);
    if (type === 'pcc') { (g as any).fillStyle(hexToNum('#445544')); (g as any).fillRect(cx - 8, 4, 16, 6); (g as any).fillRect(cx - 6, 2, 12, 3); }
    else if (type === 'fmc') { (g as any).fillStyle(hexToNum('#665544')); (g as any).fillRect(cx - 8, 4, 16, 6); (g as any).fillRect(cx - 6, 2, 12, 3); }
    else { (g as any).fillStyle(hexToNum('#554433')); (g as any).fillRect(cx - 7, 3, 14, 7); }
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
    (g as any).lineStyle(1, hexToNum('#333333')); g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();
    (g as any).fillStyle(colors.skin); (g as any).fillRect(cx - 3, 18, 6, 4);
    (g as any).fillStyle(colors.uniform); (g as any).fillRect(cx - 11, 22, 22, 18);
    (g as any).lineStyle(1, colors.uniformDark);
    g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
    g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
    (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillCircle(cx, 28, 1.5); (g as any).fillCircle(cx, 33, 1.5);
    (g as any).fillStyle(colors.uniform); (g as any).fillRect(cx - 15, 23, 5, 14); (g as any).fillRect(cx + 10, 23, 5, 14);
    (g as any).fillStyle(colors.skin); (g as any).fillCircle(cx - 12, 38, 3); (g as any).fillCircle(cx + 12, 38, 3);
    (g as any).fillStyle(hexToNum('#333333')); (g as any).fillRect(cx - 11, 38, 22, 3); (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillRect(cx - 2, 38, 4, 3);
    (g as any).fillStyle(colors.pants); (g as any).fillRect(cx - 9, 41, 7, 10); (g as any).fillRect(cx + 2, 41, 7, 10);
    (g as any).fillStyle(hexToNum('#333333')); (g as any).fillRect(cx - 10, 49, 9, 5); (g as any).fillRect(cx + 1, 49, 9, 5);
    return g;
  }

  private createEnemyRunFrame2(type: 'pcc' | 'fmc' | 'ujc'): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 20;
    const colors = this.getEnemyColors(type);
    (g as any).fillStyle(colors.skin); (g as any).fillCircle(cx, 12, 8);
    if (type === 'pcc') { (g as any).fillStyle(hexToNum('#445544')); (g as any).fillRect(cx - 8, 4, 16, 6); (g as any).fillRect(cx - 6, 2, 12, 3); }
    else if (type === 'fmc') { (g as any).fillStyle(hexToNum('#665544')); (g as any).fillRect(cx - 8, 4, 16, 6); (g as any).fillRect(cx - 6, 2, 12, 3); }
    else { (g as any).fillStyle(hexToNum('#554433')); (g as any).fillRect(cx - 7, 3, 14, 7); }
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
    (g as any).lineStyle(1, hexToNum('#333333')); g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();
    (g as any).fillStyle(colors.skin); (g as any).fillRect(cx - 3, 18, 6, 4);
    (g as any).fillStyle(colors.uniform); (g as any).fillRect(cx - 11, 22, 22, 18);
    (g as any).lineStyle(1, colors.uniformDark);
    g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
    g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
    (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillCircle(cx, 28, 1.5); (g as any).fillCircle(cx, 33, 1.5);
    (g as any).fillStyle(colors.uniform); (g as any).fillRect(cx - 15, 23, 5, 14); (g as any).fillRect(cx + 10, 23, 5, 14);
    (g as any).fillStyle(colors.skin); (g as any).fillCircle(cx - 12, 38, 3); (g as any).fillCircle(cx + 12, 38, 3);
    (g as any).fillStyle(hexToNum('#333333')); (g as any).fillRect(cx - 11, 38, 22, 3); (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillRect(cx - 2, 38, 4, 3);
    (g as any).fillStyle(colors.pants); (g as any).fillRect(cx - 4, 41, 6, 8); (g as any).fillRect(cx - 8, 41, 6, 8);
    (g as any).fillStyle(hexToNum('#333333')); (g as any).fillRect(cx + 3, 47, 8, 7); (g as any).fillRect(cx - 11, 47, 8, 7);
    return g;
  }

  private createEnemyRunFrame3(type: 'pcc' | 'fmc' | 'ujc'): Phaser.GameObjects.Graphics {
    return this.createEnemyRunFrame1(type);
  }

  private createEnemyAttackSprite(type: 'pcc' | 'fmc' | 'ujc'): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 20;
    const colors = this.getEnemyColors(type);
    (g as any).fillStyle(colors.skin); (g as any).fillCircle(cx, 12, 8);
    if (type === 'pcc') { (g as any).fillStyle(hexToNum('#445544')); (g as any).fillRect(cx - 8, 4, 16, 6); (g as any).fillRect(cx - 6, 2, 12, 3); }
    else if (type === 'fmc') { (g as any).fillStyle(hexToNum('#665544')); (g as any).fillRect(cx - 8, 4, 16, 6); (g as any).fillRect(cx - 6, 2, 12, 3); }
    else { (g as any).fillStyle(hexToNum('#554433')); (g as any).fillRect(cx - 7, 3, 14, 7); }
    (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
    (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
    (g as any).lineStyle(1, hexToNum('#333333')); g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();
    (g as any).fillStyle(colors.skin); (g as any).fillRect(cx - 3, 18, 6, 4);
    (g as any).fillStyle(colors.uniform); (g as any).fillRect(cx - 11, 22, 22, 18);
    (g as any).lineStyle(1, colors.uniformDark);
    g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
    g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
    (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillCircle(cx, 28, 1.5); (g as any).fillCircle(cx, 33, 1.5);
    // Arms extended (attack pose)
    (g as any).fillStyle(colors.uniform); (g as any).fillRect(cx - 15, 23, 5, 14);
    (g as any).fillRect(cx + 10, 23, 12, 5);
    (g as any).fillStyle(colors.skin); (g as any).fillCircle(cx - 12, 38, 3); (g as any).fillCircle(cx + 22, 25, 3);
    (g as any).fillStyle(hexToNum('#333333')); (g as any).fillRect(cx - 11, 38, 22, 3); (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillRect(cx - 2, 38, 4, 3);
    (g as any).fillStyle(colors.pants); (g as any).fillRect(cx - 9, 41, 7, 10); (g as any).fillRect(cx + 2, 41, 7, 10);
    (g as any).fillStyle(hexToNum('#333333')); (g as any).fillRect(cx - 10, 49, 9, 5); (g as any).fillRect(cx + 1, 49, 9, 5);
    return g;
  }

  private createNpcSprite(type: string): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const cx = 18;

    if (type === 'tourist') {
      // Tourist: hat, camera, casual clothes
      (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx, 12, 8);
      (g as any).fillStyle(hexToNum('#886644')); (g as any).fillRect(cx - 10, 4, 20, 4);
      (g as any).fillRect(cx - 5, 0, 10, 5);
      (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
      (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
      // Smile
      (g as any).lineStyle(1, hexToNum('#333333')); g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();
      (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillRect(cx - 3, 18, 6, 4);
      (g as any).fillStyle(hexToNum('#dd8844')); (g as any).fillRect(cx - 10, 22, 20, 18);
      (g as any).lineStyle(1, hexToNum('#cc7733'));
      g.beginPath(); g.moveTo(cx - 5, 26); g.lineTo(cx - 5, 38); g.strokePath();
      g.beginPath(); g.moveTo(cx + 5, 26); g.lineTo(cx + 5, 38); g.strokePath();
      // Camera around neck
      (g as any).fillStyle(hexToNum('#333333')); (g as any).fillCircle(cx, 26, 3);
      (g as any).fillStyle(hexToNum('#555555')); (g as any).fillCircle(cx, 26, 1.5);
      (g as any).fillStyle(hexToNum('#dd8844')); (g as any).fillRect(cx - 14, 23, 5, 14); (g as any).fillRect(cx + 9, 23, 5, 14);
      (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx - 11, 38, 3); (g as any).fillCircle(cx + 11, 38, 3);
      (g as any).fillStyle(hexToNum('#556677')); (g as any).fillRect(cx - 8, 41, 7, 10); (g as any).fillRect(cx + 1, 41, 7, 10);
      (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(cx - 9, 49, 8, 5); (g as any).fillRect(cx + 1, 49, 8, 5);
    } else if (type === 'pioneer') {
      // Pioneer: military-style, cap
      (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx, 12, 8);
      (g as any).fillStyle(hexToNum('#556644')); (g as any).fillRect(cx - 8, 3, 16, 7);
      (g as any).fillRect(cx - 6, 1, 12, 3);
      (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
      (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
      (g as any).lineStyle(1, hexToNum('#333333')); g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();
      (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillRect(cx - 3, 18, 6, 4);
      (g as any).fillStyle(hexToNum('#556644')); (g as any).fillRect(cx - 9, 22, 18, 18);
      (g as any).lineStyle(1, hexToNum('#445533'));
      g.beginPath(); g.moveTo(cx - 4, 26); g.lineTo(cx - 4, 38); g.strokePath();
      g.beginPath(); g.moveTo(cx + 4, 26); g.lineTo(cx + 4, 38); g.strokePath();
      (g as any).fillStyle(hexToNum('#ddaa44')); (g as any).fillCircle(cx, 28, 1.5); (g as any).fillCircle(cx, 33, 1.5);
      (g as any).fillStyle(hexToNum('#556644')); (g as any).fillRect(cx - 13, 23, 5, 14); (g as any).fillRect(cx + 8, 23, 5, 14);
      (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx - 10, 38, 3); (g as any).fillCircle(cx + 10, 38, 3);
      (g as any).fillStyle(hexToNum('#445533')); (g as any).fillRect(cx - 9, 38, 18, 3);
      (g as any).fillStyle(hexToNum('#556644')); (g as any).fillRect(cx - 7, 41, 6, 10); (g as any).fillRect(cx + 1, 41, 6, 10);
      (g as any).fillStyle(hexToNum('#333333')); (g as any).fillRect(cx - 8, 49, 7, 5); (g as any).fillRect(cx + 1, 49, 7, 5);
    } else {
      // Civilian: simple clothes
      (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx, 12, 8);
      (g as any).fillStyle(hexToNum('#443322')); (g as any).fillCircle(cx, 9, 7); (g as any).fillRect(cx - 7, 5, 14, 5);
      (g as any).fillStyle(0xffffff); (g as any).fillCircle(cx - 3, 11, 2); (g as any).fillCircle(cx + 3, 11, 2);
      (g as any).fillStyle(hexToNum('#222222')); (g as any).fillCircle(cx - 2, 11, 1); (g as any).fillCircle(cx + 4, 11, 1);
      (g as any).lineStyle(1, hexToNum('#333333')); g.beginPath(); g.moveTo(cx - 3, 15); g.lineTo(cx + 3, 15); g.strokePath();
      (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillRect(cx - 3, 18, 6, 4);
      (g as any).fillStyle(hexToNum('#887766')); (g as any).fillRect(cx - 10, 22, 20, 18);
      (g as any).fillStyle(hexToNum('#887766')); (g as any).fillRect(cx - 14, 23, 5, 14); (g as any).fillRect(cx + 9, 23, 5, 14);
      (g as any).fillStyle(hexToNum('#d4a574')); (g as any).fillCircle(cx - 11, 38, 3); (g as any).fillCircle(cx + 11, 38, 3);
      (g as any).fillStyle(hexToNum('#665544')); (g as any).fillRect(cx - 8, 41, 7, 10); (g as any).fillRect(cx + 1, 41, 7, 10);
      (g as any).fillStyle(hexToNum('#553322')); (g as any).fillRect(cx - 9, 49, 8, 5); (g as any).fillRect(cx + 1, 49, 8, 5);
    }

    return g;
  }

  private generateBackground(): void {
    // Only generate procedural bg if the external DrawThings PNG is NOT loaded.
    if (!this.scene.textures.exists('sky')) {
      const sky = this.createSkyGradient();
      sky.generateTexture('bg_sky', CONSTANTS.WORLD_WIDTH, 720);
      sky.destroy();
    }

    if (!this.scene.textures.exists('buildings_far')) {
      const buildingsFar = this.createBuildingsFar();
      (buildingsFar as any).generateTexture('bg_buildings_far', CONSTANTS.WORLD_WIDTH, 720);
      (buildingsFar as any).destroy();
    }

    if (!this.scene.textures.exists('buildings_mid')) {
      const buildingsMid = this.createBuildingsMid();
      buildingsMid.generateTexture('bg_buildings_mid', CONSTANTS.WORLD_WIDTH, 720);
      buildingsMid.destroy();
    }

    if (!this.scene.textures.exists('clouds')) {
      const clouds = this.createClouds();
      clouds.generateTexture('bg_clouds', CONSTANTS.WORLD_WIDTH, 720);
      clouds.destroy();
    }

    const groundTex = this.createGroundTexture();
    groundTex.generateTexture('bg_ground', CONSTANTS.WORLD_WIDTH, 40);
    groundTex.destroy();

    const waterTex = this.createWaterTexture();
    waterTex.generateTexture('bg_water', 200, 40);
    waterTex.destroy();

    this.cacheUsed = true;
  }

  // ============================================================
  // Layer 0: Caribbean sky gradient — deep blue (#87CEEB) →
  // warm turquoise (#B0E0E6) → pale golden horizon (#F5E6C8)
  // using a 3-stop vertical gradient for cinematic atmospheric depth.
  // ============================================================
  private createSkyGradient(): Phaser.GameObjects.Graphics {
    const g = this.scene.make.graphics({ x: 0, y: 0 });
    const cw = CONSTANTS.WORLD_WIDTH;

    // 3-stop vertical gradient: deep Caribbean blue → warm turquoise → golden horizon
    (g as any).fillGradientStyle(
      hexToNum('#6a9fd8'),   // top: deep sky blue
      hexToNum('#a8d4e6'),   // mid: Caribbean turquoise
      hexToNum('#f5e6c8'),   // horizon: warm Havana golden
      0, 0.5, 1,            // stops
      0, 1                  // vertical
    );
    (g as any).fillRect(0, 0, cw, 720);

    return g;
  }

  // ============================================================
  // Layer 2: Distant Havana skyline — atmospheric perspective
  // with blue-gray haze (#708090), recognisable silhouettes:
  // Capitolio dome, Gran Teatro towers, church spires.
  // Buildings are deterministic (SeededRandom) so the world
  // scrolls seamlessly without visible seams.
  // ============================================================
  private createBuildingsFar(): Phaser.GameObjects.Graphics {
    const g = this.scene.make.graphics({ x: 0, y: 0 });
    const cw = CONSTANTS.WORLD_WIDTH;
    const groundY = 680;

    // Atmospheric haze gradient (lighter near horizon, darker at top)
    (g as any).fillGradientStyle(
      hexToNum('#9ab0c8'),
      hexToNum('#c8d8e8'),
      hexToNum('#dce8d8'),
      0, 0.4, 1,
      0, 1
    );
    (g as any).fillRect(0, 0, cw, 720);

    // Color palette: desaturated blue-gray for atmospheric perspective
    const buildingColor = hexToNum('#708090');
    const buildingShadow = hexToNum('#5a6a7a');
    const windowColor = hexToNum('#8899aa');

    let x = 0;
    // Seed a deterministic building layout that tiles at WORLD_WIDTH
    const seed = 42;
    const seeded = new SeededRandom(seed);

    while (x < cw * 2.5) {
      const bw = 50 + seeded.nextInt(40, 110);
      const bh = 160 + seeded.nextInt(140, 340);
      const by = groundY - bh;

      // Building body
      (g as any).fillStyle(buildingColor);
      (g as any).fillRect(x, by, bw, bh);

      // Subtle right-side shadow for depth
      (g as any).fillStyle(buildingShadow);
      (g as any).fillRect(x + bw - 8, by, 8, bh);

      // Windows (dim, few and large — distant buildings, low storeys)
      const winW = 10;
      const winH = 14;
      for (let wy = by + 14; wy < groundY - 18; wy += 32) {
        for (let wx = x + 8; wx < x + bw - 14; wx += 20) {
          (g as any).fillStyle(seeded.nextBool() ? windowColor : hexToNum('#5a6a7a'));
          (g as any).fillRect(wx, wy, winW, winH);
        }
      }

      // === LANDMARKS (deterministic placement) ===

      // Capitolio dome — place at a fixed offset from world start
      const capitolioX = 600;
      if (x <= capitolioX && x + bw >= capitolioX) {
        const domeX = capitolioX;
        const domeY = by;
        const domeR = Math.min(bw / 2, 30);
        // Dome base
        (g as any).fillStyle(buildingColor);
        (g as any).fillRect(domeX - domeR, domeY, domeR * 2, 8);
        // Dome (semi-circle)
        (g as any).fillStyle(buildingColor);
        (g as any).fillEllipse(domeX, domeY, domeR * 2, domeR);
        // Spire
        (g as any).fillStyle(buildingShadow);
        (g as any).fillRect(domeX - 1.5, domeY - 25, 3, 25);
        (g as any).fillCircle(domeX, domeY - 25, 2.5);
      }

      // Church tower — place 1200px from start
      const towerX = 1200;
      if (x <= towerX && x + bw >= towerX) {
        const towerW = 16;
        const towerH = 180;
        const towerBy = groundY - towerH;
        (g as any).fillStyle(buildingColor);
        (g as any).fillRect(towerX - towerW / 2, towerBy, towerW, towerH);
        // Spire
        (g as any).fillStyle(buildingShadow);
        (g as any).fillRect(towerX - 2, towerBy - 20, 4, 20);
        (g as any).fillCircle(towerX, towerBy - 20, 3);
      }

      // Generic tall tower (Gran Teatro style) — place 2000px from start
      const tallX = 2000;
      if (x <= tallX && x + bw >= tallX) {
        const tallW = 22;
        const tallH = 200;
        const tallBy = groundY - tallH;
        (g as any).fillStyle(buildingColor);
        (g as any).fillRect(tallX - tallW / 2, tallBy, tallW, tallH);
        // Ornamental top
        (g as any).fillStyle(buildingShadow);
        (g as any).fillRect(tallX - 6, tallBy - 12, 12, 12);
        (g as any).fillRect(tallX - 3, tallBy - 20, 6, 8);
      }

      x += bw + 4 + seeded.nextInt(4, 20);
    }

    return g;
  }

  // ============================================================
  // Layer 3: Mid buildings — colonial/urban Havana facades with:
  //  • Cream (#F5DEB3), terracotta (#CD853F), aged yellow (#E4D00A)
  //  • Dark rectangular windows, balcony railings
  //  • Cornice lines at roof tops
  //  • Weathered color variation per building
  //  • Deterministic layout (no random!) so the world scrolls seamlessly.
  // ============================================================
  private createBuildingsMid(): Phaser.GameObjects.Graphics {
    const g = this.scene.make.graphics({ x: 0, y: 0 });
    const cw = CONSTANTS.WORLD_WIDTH;
    const groundY = 680;

    // Deterministic layout seed (same as far buildings for layer alignment)
    const seeded = new SeededRandom(42);

    let x = 0;
    while (x < cw * 2.5) {
      const bw = 60 + seeded.nextInt(50, 150);
      // Havana buildings: low with few storeys, but keep them readable.
      const bh = 180 + seeded.nextInt(120, 320);
      const by = groundY - bh;

      // Facade color palette: Havana pastel tones (light yellow, salmon,
      // cyan, light gray) with per-building variation
      const facades = [
        hexToNum('#F6E7A0'), // light yellow
        hexToNum('#F8B8A8'), // salmon
        hexToNum('#9FD9E0'), // cyan
        hexToNum('#D8DCE0'), // light gray
      ];
      const facadeColor = facades[seeded.nextInt(0, facades.length)];
      const facadeDark = hexToNum(
        '#' + facadeColor.toString(16).padStart(6, '0').slice(0, 6).replace(
          /(..)(..)(..)/,
          (_, r, g, b) => `${Math.max(0, parseInt(r, 16) - 30).toString(16).padStart(2, '0')}${Math.max(0, parseInt(g, 16) - 30).toString(16).padStart(2, '0')}${Math.max(0, parseInt(b, 16) - 30).toString(16).padStart(2, '0')}`
        ) || '000000'
      );

      // Main building body
      (g as any).fillStyle(facadeColor);
      (g as any).fillRect(x, by, bw, bh);

      // Weathering: subtle vertical streaks (water stains)
      (g as any).fillStyle(facadeDark, 0.3);
      const streakX = x + 3 + seeded.nextInt(0, Math.max(1, bw - 6));
      (g as any).fillRect(streakX, by + 20, 2, bh - 20);
      (g as any).fillRect(x + bw - 5, by + 30, 2, bh - 30);

      // Shadow on right side (depth)
      (g as any).fillStyle(facadeDark, 0.4);
      (g as any).fillRect(x + bw - 10, by, 10, bh);

      // === COLONIAL ARCADE (1-3 arches, ALL the same fixed size) ===
      // Number of arches depends on how many fixed-width arches fit in the
      // building: a building with a single arch is simply too narrow for more.
      const arcadeH = 46;
      const arcadeTop = groundY - arcadeH;
      const archW = 50;       // fixed arch width (same for every arch)
      const archGap = 10;     // spacing between arches
      const nArches = Math.max(1, Math.min(3, Math.floor((bw + archGap - 2) / (archW + archGap))));
      const totalArchesW = nArches * archW + (nArches - 1) * archGap;
      const arcadeX = x + Math.floor((bw - totalArchesW) / 2);
      // Arcade wall background (slightly darker alcove behind the arches)
      (g as any).fillStyle(facadeDark, 0.35);
      (g as any).fillRect(x, arcadeTop, bw, arcadeH);
      for (let a = 0; a < nArches; a++) {
        const ax = arcadeX + a * (archW + archGap);
        const aw = archW - 8;
        const awcx = ax + archW / 2;
        (g as any).fillStyle(facadeColor);
        (g as any).fillRect(ax, arcadeTop, archW, arcadeH);
        // Arch opening (deep dark)
        (g as any).fillStyle(hexToNum('#3a3a3a'));
        (g as any).fillRect(ax + 5, arcadeTop + 12, aw - 6, arcadeH - 12);
        // Arch semicircle on top of the opening
        (g as any).fillRect(ax + 5, arcadeTop, aw - 6, 12);
        (g as any).fillCircle(awcx, arcadeTop, aw / 2 - 4);
        // Arch rim (light outline)
        (g as any).lineStyle(2, facadeColor);
        (g as any).strokeEllipse(awcx, arcadeTop, (aw - 4) * 2, (aw - 4));
        // Arch base / column separation
        (g as any).fillStyle(facadeDark);
        (g as any).fillRect(ax, arcadeTop - 4, archW, 4);
      }

      // === WINDOWS (dark rectangles, grid pattern) ===
      // Where there are arches there are no windows: the first two window
      // rows above the arcade are omitted (replaced by the arches).
      const winW = 22;
      const winH = 28;
      const floorH = 44;
      const winTop = arcadeTop - 2 * floorH;
      const winColor = hexToNum('#3a3a3a');
      const litWinColor = hexToNum('#ffeeaa');
      const colSpacing = 34;
      for (let wy = by + 18; wy < winTop; wy += floorH) {
        for (let wx = x + 12; wx < x + bw - 26; wx += colSpacing) {
          (g as any).fillStyle(seeded.nextBool() ? litWinColor : winColor);
          (g as any).fillRect(wx, wy, winW, winH);
          // Window frame
          (g as any).lineStyle(2, facadeDark);
          (g as any).strokeRect(wx, wy, winW, winH);
          // Window shutter/divider (vertical bar)
          (g as any).fillStyle(facadeDark);
          (g as any).fillRect(wx + winW / 2 - 1, wy, 2, winH);
        }
      }

      // === BALCONIES (colonial wrought-iron style) ===
      if (seeded.nextBool()) {
        const balconyY = by + 70;
        const balconyH = 14;
        // Balcony floor
        (g as any).fillStyle(hexToNum('#8B7355'));
        (g as any).fillRect(x + 6, balconyY, bw - 12, 4);
        // Railing posts
        (g as any).fillStyle(hexToNum('#444444'));
        for (let bx = x + 10; bx < x + bw - 10; bx += 8) {
          (g as any).fillRect(bx, balconyY - 8, 2, balconyH);
        }
        // Railing top bar
        (g as any).fillRect(x + 10, balconyY - 8, bw - 20, 2);
      }

      // === CORNICE (architectural roof trim) ===
      (g as any).fillStyle(facadeDark);
      (g as any).fillRect(x - 2, by, bw + 4, 6);
      (g as any).fillRect(x, by + 6, bw, 3);
      // Decorative cornice blocks
      for (let cx = x + 5; cx < x + bw - 5; cx += 15) {
        (g as any).fillRect(cx, by + 3, 6, 3);
      }

      // === ROOF DETAILS (flat roof with parapet) ===
      (g as any).fillStyle(facadeDark);
      (g as any).fillRect(x, by - 8, bw, 8);
      // Small water tanks on roof
      if (seeded.nextBool()) {
        const tankX = x + 5 + seeded.nextInt(0, bw - 20);
        (g as any).fillStyle(hexToNum('#666666'));
        (g as any).fillRect(tankX, by - 18, 14, 10);
        (g as any).fillRect(tankX + 2, by - 22, 10, 4);
      }

      x += bw + 5 + seeded.nextInt(5, 25);
    }

    return g;
  }

  // ============================================================
  // Layer 1: Volumetric clouds — organic overlapping shapes
  // with warm Caribbean tint (slight ivory/cream), varying sizes,
  // distributed horizontally across the world width.
  // Uses multiple overlapping ellipses per cloud for puffiness.
  // ============================================================
  private createClouds(): Phaser.GameObjects.Graphics {
    const g = this.scene.make.graphics({ x: 0, y: 0 });
    const cw = CONSTANTS.WORLD_WIDTH;
    const rng = new SeededRandom(1234);

    // Draw organic puffy clouds across the sky
    let cx = 0;
    while (cx < cw * 2) {
      // Each cloud is a cluster of overlapping ellipses for a volumetric look
      const cloudW = 80 + rng.nextInt(0, 160);
      const cloudH = 25 + rng.nextInt(0, 40);
      const cy = 40 + rng.nextInt(0, 200);

      // Cloud tint: warm Caribbean ivory (slightly warm, not pure white)
      const cloudColor = hexToNum('#fef8f0');
      const cloudShadow = hexToNum('#e8dcc8');

      // Main puff
      (g as any).fillStyle(cloudColor, 0.85);
      (g as any).fillEllipse(cx + cloudW / 2, cy, cloudW, cloudH);

      // Additional puffs for organic shape
      (g as any).fillEllipse(cx + cloudW * 0.2, cy - cloudH * 0.2, cloudW * 0.55, cloudH * 0.65);
      (g as any).fillEllipse(cx + cloudW * 0.75, cy - cloudH * 0.15, cloudW * 0.5, cloudH * 0.6);
      (g as any).fillEllipse(cx + cloudW * 0.5, cy - cloudH * 0.3, cloudW * 0.6, cloudH * 0.55);

      // Subtle shadow underneath (depth)
      (g as any).fillStyle(cloudShadow, 0.25);
      (g as any).fillEllipse(cx + cloudW / 2, cy + cloudH * 0.3, cloudW * 0.8, cloudH * 0.3);

      cx += cloudW * 0.6 + rng.nextInt(40, 180);
    }

    return g;
  }

  private createGroundTexture(): Phaser.GameObjects.Graphics {
    const g = this.scene.make.graphics({ x: 0, y: 0 });
    const cw = CONSTANTS.WORLD_WIDTH;

    // Base: warm Havana street (worn asphalt/concrete)
    (g as any).fillGradientStyle(
      hexToNum('#8a8a7a'),   // top: lighter worn surface
      hexToNum('#6a6a5a'),   // mid
      hexToNum('#5a5a4a'),   // bottom: darker edge
      0, 0.4, 1,
      0, 1
    );
    (g as any).fillRect(0, 0, cw, 40);

    // Surface noise: scattered small dots for wear
    const seeded = new SeededRandom(777);
    for (let i = 0; i < 300; i++) {
      const nx = seeded.nextInt(0, cw);
      const ny = seeded.nextInt(0, 40);
      const shade = 55 + seeded.nextInt(0, 40);
      (g as any).fillStyle(hexToNum(`#${shade.toString(16).padStart(2, '0')}${shade.toString(16).padStart(2, '0')}${(shade - 10).toString(16).padStart(2, '0')}`), 0.5);
      (g as any).fillRect(nx, ny, 1, 1);
    }

    // Cracks: thin dark lines scattered across the street
    (g as any).lineStyle(0.5, hexToNum('#3a3a2a'), 0.6);
    for (let i = 0; i < 25; i++) {
      const sx = seeded.nextInt(0, cw);
      const sy = seeded.nextInt(5, 35);
      const len = 8 + seeded.nextInt(0, 25);
      const angle = seeded.nextFloat() * Math.PI;
      g.beginPath();
      g.moveTo(sx, sy);
      g.lineTo(sx + Math.cos(angle) * len, sy + Math.sin(angle) * len);
      g.strokePath();
    }

    // Top gutter line: slightly raised curb (subtle highlight)
    (g as any).lineStyle(1.5, hexToNum('#9a9a8a'), 0.7);
    g.beginPath();
    g.moveTo(0, 1);
    for (let gx = 0; gx < cw; gx += 15) {
      g.lineTo(gx, 1 + seeded.nextFloat() * 0.5);
    }
    g.strokePath();

    // Bottom edge: darker shadow line (gutter)
    (g as any).lineStyle(2, hexToNum('#4a4a3a'), 0.8);
    g.beginPath();
    g.moveTo(0, 38);
    g.lineTo(cw, 38);
    g.strokePath();

    // Occasional larger cracks (worn street patches)
    (g as any).lineStyle(1, hexToNum('#4a4a3a'), 0.5);
    for (let i = 0; i < 8; i++) {
      const px = seeded.nextInt(0, cw);
      const py = seeded.nextInt(10, 30);
      g.beginPath();
      g.moveTo(px, py);
      g.lineTo(px + 20 + seeded.nextInt(0, 30), py + seeded.nextInt(-5, 5));
      g.strokePath();
    }

    return g;
  }

  private createWaterTexture(): Phaser.GameObjects.Graphics {
    const g = this.scene.make.graphics({ x: 0, y: 0 });
    const pw = 200;

    // Sea water: rich blue gradient with translucent shimmer bands.
    (g as any).fillStyle(hexToNum('#1a5ca8'));
    (g as any).fillRect(0, 0, pw, 40);
    (g as any).fillStyle(hexToNum('#2b7fd0'), 0.6);
    (g as any).fillRect(0, 0, pw, 10);
    // Surface highlight waves
    (g as any).fillStyle(hexToNum('#7fc4e8'), 0.7);
    for (let wx = 0; wx < pw; wx += 16) {
      (g as any).fillRect(wx + 2, 2, 8, 2);
    }
    // Foam edge near the surface
    (g as any).fillStyle(hexToNum('#cfeeff'), 0.8);
    (g as any).fillRect(0, 0, pw, 2);
    // Deeper speckles (bubbles)
    const seeded = new SeededRandom(555);
    for (let i = 0; i < 30; i++) {
      const wx = seeded.nextInt(0, pw);
      const wy = 12 + seeded.nextInt(0, 26);
      (g as any).fillStyle(hexToNum('#6fb0e8'), 0.4);
      (g as any).fillRect(wx, wy, 2, 1);
    }

    return g;
  }

  private generatePlatforms(): void {
    if (!this.scene.textures.exists('platform-ground')) {
      const groundG = this.createPlatformTexture(40);
      groundG.generateTexture('platform-ground', 200, 40);
      groundG.destroy();
    }

    if (!this.scene.textures.exists('platform')) {
      const elevG = this.createElevatedPlatformTexture(32);
      elevG.generateTexture('platform', 200, 32);
      elevG.destroy();
    }
  }

  // Ground platform: thick colonial balcony with detailed molding
  private createPlatformTexture(height: number): Phaser.GameObjects.Graphics {
    const g = this.scene.make.graphics({ x: 0, y: 0 });
    const pw = 200;

    // Main surface: warm stone/concrete gradient (full opaque body first)
    (g as any).fillStyle(hexToNum('#a89878'));
    (g as any).fillRect(0, 0, pw, height);
    // Painted gradient bands: lighter at top, darker at bottom
    (g as any).fillStyle(hexToNum('#c8b898'));
    (g as any).fillRect(0, 0, pw, height * 0.5);
    (g as any).fillStyle(hexToNum('#b0a080'));
    (g as any).fillRect(0, height * 0.5, pw, height * 0.25);

    // Top edge highlight (lighter surface)
    (g as any).lineStyle(1, hexToNum('#d8c8a8'), 0.6);
    g.beginPath(); g.moveTo(0, 1); g.lineTo(pw, 1); g.strokePath();

    // Shadow below platform (3D depth effect)
    (g as any).fillStyle(hexToNum('#6a5a3a'), 0.6);
    (g as any).fillRect(0, height - 4, pw, 4);

    // Decorative molding at bottom edge (colonial detail)
    (g as any).lineStyle(1, hexToNum('#7a6a4a'), 0.7);
    g.beginPath(); g.moveTo(0, height - 6); g.lineTo(pw, height - 6); g.strokePath();

    // Small decorative blocks along the molding
    for (let bx = 5; bx < pw; bx += 15) {
      (g as any).fillRect(bx, height - 5, 8, 2);
    }

    // Surface texture (small bumps)
    for (let i = 0; i < 30; i++) {
      const tx = i * 7 + 3;
      const ty = 2 + (i % 3) * 2;
      (g as any).fillStyle(hexToNum('#b8a888'), 0.4);
      (g as any).fillRect(tx, ty, 1 + (i % 2), 1 + (i % 2));
    }

    return g;
  }

  // Elevated platform: thick floatable slab with a bright top surface so the
  // walkable edge (where the player's feet land) is clearly visible.
  private createElevatedPlatformTexture(height: number): Phaser.GameObjects.Graphics {
    const g = this.scene.make.graphics({ x: 0, y: 0 });
    const pw = 200;

    // Main body: deep green gradient, fully opaque (contrasts with the pastel
    // buildings so the platform reads clearly as a surface).
    (g as any).fillStyle(hexToNum('#1f4d2d'));
    (g as any).fillRect(0, 0, pw, height);
    (g as any).fillStyle(hexToNum('#2f6e3f'));
    (g as any).fillRect(0, 0, pw, height * 0.5);

    // Bright top surface strip (walkable edge — feet land here) ~6px thick
    (g as any).fillStyle(hexToNum('#3f8a51'));
    (g as any).fillRect(0, 0, pw, 6);
    (g as any).lineStyle(2, hexToNum('#8fce9f'), 0.9);
    g.beginPath(); g.moveTo(0, 1); g.lineTo(pw, 1); g.strokePath();

    // Surface texture bumps (upper half only, not the dark bottom)
    for (let i = 0; i < 24; i++) {
      const tx = i * 9 + 4;
      const ty = 3 + (i % 4);
      (g as any).fillStyle(hexToNum('#4ba064'), 0.5);
      (g as any).fillRect(tx, ty, 2, 2);
    }

    // Bottom shadow (depth)
    (g as any).fillStyle(hexToNum('#12331c'), 0.8);
    (g as any).fillRect(0, height - 6, pw, 6);

    // Colonial molding just above the bottom shadow
    (g as any).lineStyle(2, hexToNum('#2a5e38'), 0.9);
    g.beginPath(); g.moveTo(0, height - 8); g.lineTo(pw, height - 8); g.strokePath();
    for (let bx = 5; bx < pw; bx += 14) {
      (g as any).fillStyle(hexToNum('#2a5e38'), 0.6);
      (g as any).fillRect(bx, height - 6, 8, 2);
    }

    return g;
  }

  private generateExitGoal(): void {
    const g = this.scene.make.graphics({ x: 0, y: 0 });

    // Pole (metallic gray)
    (g as any).fillStyle(hexToNum('#777777'));
    (g as any).fillRect(18, 10, 6, 70);
    // Pole highlight
    (g as any).fillStyle(hexToNum('#999999'), 0.5);
    (g as any).fillRect(18, 10, 2, 70);

    // Decorative base
    (g as any).fillStyle(hexToNum('#555555'));
    (g as any).fillRect(10, 76, 22, 6);
    (g as any).fillRect(12, 74, 18, 3);

    // Sign board: blue and white Havana colors
    const boardX = 4;
    const boardY = 12;
    const boardW = 52;
    const boardH = 48;

    // Board shadow
    (g as any).fillStyle(hexToNum('#334466'), 0.5);
    (g as any).fillRect(boardX + 2, boardY + 2, boardW, boardH);

    // Board background: blue
    (g as any).fillStyle(hexToNum('#2255aa'));
    (g as any).fillRoundedRect(boardX, boardY, boardW, boardH, 3);

    // Board border
    (g as any).lineStyle(2, hexToNum('#ffffff'));
    (g as any).strokeRoundedRect(boardX, boardY, boardW, boardH, 3);

    // Inner decorative border
    (g as any).lineStyle(1, hexToNum('#4488dd'), 0.6);
    (g as any).strokeRoundedRect(boardX + 3, boardY + 3, boardW - 6, boardH - 6, 2);

    // "MALECÓN" text (rendered as white rectangles for pixel-style text)
    (g as any).fillStyle(hexToNum('#ffffff'));
    // M
    (g as any).fillRect(boardX + 8, boardY + 18, 3, 12);
    (g as any).fillRect(boardX + 11, boardY + 18, 3, 12);
    (g as any).fillRect(boardX + 8, boardY + 24, 6, 2);
    (g as any).fillRect(boardX + 11, boardY + 26, 3, 4);
    // A
    (g as any).fillRect(boardX + 18, boardY + 18, 3, 12);
    (g as any).fillRect(boardX + 21, boardY + 18, 3, 12);
    (g as any).fillRect(boardX + 18, boardY + 26, 6, 2);
    (g as any).fillRect(boardX + 21, boardY + 24, 3, 2);
    // L
    (g as any).fillRect(boardX + 28, boardY + 18, 3, 12);
    (g as any).fillRect(boardX + 28, boardY + 28, 6, 2);
    // E
    (g as any).fillRect(boardX + 38, boardY + 18, 3, 12);
    (g as any).fillRect(boardX + 38, boardY + 20, 5, 2);
    (g as any).fillRect(boardX + 38, boardY + 26, 5, 2);
    (g as any).fillRect(boardX + 38, boardY + 28, 5, 2);
    // C
    (g as any).fillRect(boardX + 47, boardY + 18, 3, 12);
    (g as any).fillRect(boardX + 47, boardY + 18, 5, 3);
    (g as any).fillRect(boardX + 47, boardY + 27, 5, 3);
    // O
    (g as any).fillRect(boardX + 56, boardY + 18, 3, 12);
    (g as any).fillRect(boardX + 56, boardY + 18, 5, 3);
    (g as any).fillRect(boardX + 56, boardY + 27, 5, 3);
    (g as any).fillRect(boardX + 56, boardY + 18, 3, 12);
    // N
    (g as any).fillRect(boardX + 65, boardY + 18, 3, 12);
    (g as any).fillRect(boardX + 68, boardY + 18, 3, 12);
    (g as any).fillRect(boardX + 65, boardY + 24, 6, 2);
    (g as any).fillRect(boardX + 68, boardY + 26, 3, 4);

    // Top decorative element: small Cuban-inspired star
    (g as any).fillStyle(hexToNum('#ffdd00'));
    (g as any).fillCircle(30, 8, 4);
    (g as any).fillStyle(hexToNum('#ffaa00'));
    (g as any).fillCircle(30, 8, 2);

    // Pole cap
    (g as any).fillStyle(hexToNum('#888888'));
    (g as any).fillCircle(21, 10, 4);

    (g as any).generateTexture('exit_goal', 60, 84);
  }

  private generatePowerups(): void {
    // Only generate procedural textures when external DrawThings PNGs are NOT loaded.
    if (!this.scene.textures.exists('powerup_euro'))
      this.makeCurrencyPowerup('powerup_euro', 22, 22, '#ffd700', '#ccaa00', '€', '#8a6d00', '14px');

    if (!this.scene.textures.exists('powerup_dollar'))
      this.makeCurrencyPowerup('powerup_dollar', 28, 18, '#c0c0c0', '#808080', '$', '#2a2a2a', '12px');

    if (!this.scene.textures.exists('powerup_peso'))
      this.makeCurrencyPowerup('powerup_peso', 28, 18, '#888888', '#555555', '$', '#2a2a2a', '12px');

    if (!this.scene.textures.exists('powerup_croqueta')) {
      const croquetaG = this.scene.add.graphics();
      croquetaG.fillStyle(hexToNum('#aa6633'));
      croquetaG.fillEllipse(7, 4, 14, 8);
      croquetaG.lineStyle(1, hexToNum('#884422'));
      croquetaG.strokeEllipse(7, 4, 14, 8);
      croquetaG.generateTexture('powerup_croqueta', 14, 8);
      croquetaG.destroy();
    }

    if (!this.scene.textures.exists('powerup_cafecito')) {
      const cafeG = this.scene.add.graphics();
      cafeG.fillStyle(hexToNum('#553322'));
      cafeG.fillRect(2, 0, 16, 16);
      cafeG.fillStyle(hexToNum('#442211'));
      cafeG.fillRect(0, 6, 20, 4);
      cafeG.fillStyle(hexToNum('#442211'));
      cafeG.fillRect(18, 4, 4, 8);
      cafeG.generateTexture('powerup_cafecito', 20, 24);
      cafeG.destroy();
    }

    if (!this.scene.textures.exists('powerup_mlc'))
      this.makeCurrencyPowerup('powerup_mlc', 30, 20, '#cc4444', '#882222', 'MLC', '#ffffff', '9px');

    if (!this.scene.textures.exists('powerup_ecoflow')) {
      const ecoG = this.scene.add.graphics();
      ecoG.fillStyle(hexToNum('#44aa44'));
      ecoG.fillRoundedRect(0, 2, 32, 22, 3);
      ecoG.fillRect(12, 0, 8, 4);
      ecoG.lineStyle(2, hexToNum('#338833'));
      ecoG.strokeRoundedRect(0, 2, 32, 22, 3);
      ecoG.generateTexture('powerup_ecoflow', 32, 24);
      ecoG.destroy();
    }

    // Pile of stones (ammo pickup)
    if (!this.scene.textures.exists('powerup_piedras')) {
      const pg = this.scene.add.graphics();
      const drawStone = (cx: number, cy: number, rw: number, rh: number) => {
        pg.fillStyle(hexToNum('#999999'));
        pg.fillEllipse(cx, cy, rw * 2, rh * 2);
        pg.lineStyle(1, hexToNum('#666666'));
        pg.strokeEllipse(cx, cy, rw * 2, rh * 2);
        pg.fillStyle(hexToNum('#cccccc'));
        pg.fillEllipse(cx - rw / 3, cy - rh / 3, rw / 1.5, rh / 1.5);
      };
      // Bottom row of three + two on top
      drawStone(10, 22, 7, 5);
      drawStone(18, 23, 8, 5.5);
      drawStone(26, 22, 7, 5);
      drawStone(14, 15, 6, 4.5);
      drawStone(23, 14, 6.5, 4.5);
      pg.generateTexture('powerup_piedras', 36, 28);
      pg.destroy();
    }

    if (!this.scene.textures.exists('powerup_solar-panel')) {
      const solarG = this.scene.add.graphics();
      solarG.fillStyle(hexToNum('#334488'));
      solarG.fillRoundedRect(0, 0, 36, 24, 2);
      solarG.lineStyle(1, hexToNum('#223366'));
      solarG.strokeRoundedRect(0, 0, 36, 24, 2);
      solarG.lineStyle(1, hexToNum('#445599'));
      solarG.beginPath(); solarG.moveTo(18, 0); solarG.lineTo(18, 24); solarG.strokePath();
      solarG.beginPath(); solarG.moveTo(0, 12); solarG.lineTo(36, 12); solarG.strokePath();
      solarG.generateTexture('powerup_solar-panel', 36, 24);
      solarG.destroy();
    }

    if (!this.scene.textures.exists('powerup_gas-can')) {
      const gasG = this.scene.add.graphics();
      gasG.fillStyle(hexToNum('#cc2222'));
      gasG.fillRoundedRect(0, 0, 26, 22, 2);
      gasG.fillStyle(hexToNum('#881111'));
      gasG.fillRect(8, 0, 10, 6);
      gasG.lineStyle(2, hexToNum('#881111'));
      gasG.strokeRoundedRect(0, 0, 26, 22, 2);
      gasG.generateTexture('powerup_gas-can', 26, 22);
      gasG.destroy();
    }
  }

  // Currency powerups (euro, dollar, peso, MLC): colored rounded box with a
  // text label, baked into a CanvasTexture using the 2D context (reliable).
  private makeCurrencyPowerup(
    key: string,
    w: number,
    h: number,
    fill: string,
    stroke: string,
    label: string,
    labelColor: string,
    fontSize: string
  ): void {
    const canvasTex = this.scene.textures.createCanvas(key, w, h);
    if (!canvasTex) return;
    const ctx = canvasTex.getContext();
    // Rounded rectangle body
    const radius = 4;
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(w - radius, 0);
    ctx.quadraticCurveTo(w, 0, w, radius);
    ctx.lineTo(w, h - radius);
    ctx.quadraticCurveTo(w, h, w - radius, h);
    ctx.lineTo(radius, h);
    ctx.quadraticCurveTo(0, h, 0, h - radius);
    ctx.lineTo(0, radius);
    ctx.quadraticCurveTo(0, 0, radius, 0);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = stroke;
    ctx.stroke();
    // Text label (centered)
    ctx.fillStyle = labelColor;
    ctx.font = `${fontSize} monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, w / 2, h / 2 + 1);
    canvasTex.refresh();
  }

  private generateStone(): void {
    const g = this.scene.add.graphics();
    (g as any).fillStyle(hexToNum('#888888'));
    (g as any).fillEllipse(5, 4, 10, 8);
    (g as any).lineStyle(1, hexToNum('#555555'));
    (g as any).strokeEllipse(5, 4, 10, 8);
    (g as any).generateTexture('stone', 10, 8);
  }

  private generateCheckpoints(): void {
    const g = this.scene.add.graphics();
    (g as any).fillStyle(hexToNum('#44aa44'));
    (g as any).fillRect(0, 0, 20, 40);
    (g as any).fillStyle(hexToNum('#88ff88'));
    (g as any).fillCircle(10, 10, 6);
    (g as any).lineStyle(2, hexToNum('#338833'));
    (g as any).strokeRect(0, 0, 20, 40);
    (g as any).generateTexture('checkpoint', 20, 40);
  }

  isCacheUsed(): boolean {
    return this.cacheUsed;
  }
}
